import debounce from 'debounce';

const discountTypes = {
  PERCENTAGE: 'PERCENTAGE',
  FIXED: 'FIXED',
  SET_PRICE: 'SET_PRICE',
};

const validationFields = {
  IDENTIFIER: 'identifier',
  FAMILY_NAME: 'last_name',
  GIVEN_NAME: 'first_name',
  DATE_OF_BIRTH: 'date_of_birth',
};

const formName = 'discount-validation';

const pluginName = 'discountValidationPlugin';
const defaults = {
  eventId: null,
  registrantUuid: null,
  locale: null,
  registrantPersonalInformation: null,
  routes: {
    validation: null,
  },
  callbacks: {
    initialized: () => {},
    setup: () => {},
    getGivenName: () => '',
    getFamilyName: () => '',
    getDateOfBirth: () => '',
  },
  i18n: {
    incomplete: 'Incomplete',
  },
};
const selectors = {
  snackBar: '[data-js-snack-bar]',
  form: '.js-registrant-discount-validation-form',
  pencilEditIcon: '[data-label-id]',
  selectContainer: '[data-js-discount-validation-select-container]',
  select: '[data-js-discount-validation-select]',
  personalInformationContainer: '[data-js-discount-validation-personal-information-container]',
  personalInformationFamilyNameContainer: '[data-js-discount-validation-personal-information-family-name-container]',
  personalInformationGivenNameContainer: '[data-js-discount-validation-personal-information-given-name-container]',
  personalInformationDateOfBirthContainer: '[data-js-discount-validation-personal-information-date-of-birth-container]',
  personalInformationFamilyName: '[data-js-discount-validation-personal-information-family-name]',
  personalInformationGivenName: '[data-js-discount-validation-personal-information-given-name]',
  personalInformationDateOfBirth: '[data-js-discount-validation-personal-information-date-of-birth]',
  identifierContainer: '[data-js-discount-validation-identifier-container]',
  identifier: '[data-js-discount-validation-identifier]',
  feedbackContainer: '[data-js-discount-validation-feedback-container]',
  feedbackPending: '[data-js-discount-validation-feedback-pending]',
  feedbackSuccessReduction: '[data-js-discount-validation-feedback-success-reduction]',
  feedbackSuccessReductionText: '[data-js-discount-validation-feedback-success-reduction-text]',
  feedbackSuccessSetPrice: '[data-js-discount-validation-feedback-success-set-price]',
  feedbackSuccessSetPriceText: '[data-js-discount-validation-feedback-success-set-price-text]',
  feedbackError: '[data-js-discount-validation-feedback-error]',
  submit: '[data-js-discount-validation-submit]',
};
const classes = {
  hidden: 'd-none hidden',
  incomplete: 'font-style-italic text-muted',
};

/**
 * Get the elements
 * @param data
 * @returns {{}}
 */
const getElements = (data) => {
  const $selectContainer = data.elements.$element.find(selectors.selectContainer);
  const $personalInformationContainer = data.elements.$element.find(selectors.personalInformationContainer);
  const $personalInformationFamilyNameContainer = $personalInformationContainer
    .find(selectors.personalInformationFamilyNameContainer);
  const $personalInformationGivenNameContainer = $personalInformationContainer
    .find(selectors.personalInformationGivenNameContainer);
  const $personalInformationDateOfBirthContainer = $personalInformationContainer
    .find(selectors.personalInformationDateOfBirthContainer);
  const $identifierContainer = data.elements.$element.find(selectors.identifierContainer);
  const $feedbackContainer = data.elements.$element.find(selectors.feedbackContainer);
  const $feedbackSuccessReduction = $feedbackContainer.find(selectors.feedbackSuccessReduction);
  const $feedbackSuccessReductionText = $feedbackSuccessReduction.find(selectors.feedbackSuccessReductionText);
  const $feedbackSuccessSetPrice = $feedbackContainer.find(selectors.feedbackSuccessSetPrice);
  const $feedbackSuccessSetPriceText = $feedbackSuccessSetPrice.find(selectors.feedbackSuccessSetPriceText);

  return {
    $snackBar: $(selectors.snackBar),
    $selectContainer,
    $select: $selectContainer.find(selectors.select),
    $personalInformationContainer,
    $personalInformationFamilyNameContainer,
    $personalInformationGivenNameContainer,
    $personalInformationDateOfBirthContainer,
    $personalInformationFamilyName: $personalInformationFamilyNameContainer
      .find(selectors.personalInformationFamilyName),
    $personalInformationGivenName: $personalInformationGivenNameContainer.find(selectors.personalInformationGivenName),
    $personalInformationDateOfBirth: $personalInformationDateOfBirthContainer
      .find(selectors.personalInformationDateOfBirth),
    $identifierContainer,
    $identifier: $identifierContainer.find(selectors.identifier),
    $feedbackContainer,
    $feedbackPending: $feedbackContainer.find(selectors.feedbackPending),
    $feedbackSuccessReduction,
    $feedbackSuccessReductionText,
    $feedbackSuccessReductionTextOriginal: $feedbackSuccessReductionText.clone(),
    $feedbackSuccessReductionTextPencilEditIcon: $feedbackSuccessReductionText.find(selectors.pencilEditIcon).clone(),
    $feedbackSuccessSetPrice,
    $feedbackSuccessSetPriceText,
    $feedbackSuccessSetPriceTextOriginal: $feedbackSuccessSetPriceText.clone(),
    $feedbackSuccessSetPriceTextPencilEditIcon: $feedbackSuccessSetPriceText.find(selectors.pencilEditIcon).clone(),
    $feedbackError: $feedbackContainer.find(selectors.feedbackError),
    $submit: data.elements.$element.find(selectors.submit),
  };
};

/**
 * Calls the provided custom callback and triggers the provided custom plugin event (if they exist)
 * @param data
 * @param hookName
 * @param parameters
 */
const triggerHook = (data, hookName, ...parameters) => {
  data.elements.$element
    .trigger(`${pluginName}.${hookName}`, [data.elements.$element, data, ...parameters]);
  data.settings.callbacks[hookName]
    .call(data.elements.$element, data.elements.$element, data, ...parameters);
};

/**
 * Toggle the visibility of the plugin
 * @param data
 * @param isVisible
 */
const toggleVisibility = (data, isVisible) => {
  data.elements.$element.toggleClass(classes.hidden, !isVisible);
};

/**
 * Format the currency. Right now it looks like the amount is coming from the API already in
 * decimal format, but just in case it changes to the standard sub-unit format, I'll leave the
 * commented out code
 * @param data
 * @param amount
 * @returns {string}
 */
const formatCurrency = (data, amount) => {
  // // First create a formatter to get the default decimal places for the currency
  // const defaultFormatter = new Intl.NumberFormat(data.settings.locale, {
  //   style: 'currency',
  //   currency: data.settings.currency,
  // });

  // // Get the decimal places for the currency, defaulting to 2 if undefined
  // const decimalPlaces = defaultFormatter.resolvedOptions().maximumFractionDigits ?? 2;

  // // Convert from minor units to major units based on currency configuration
  // const majorAmount = Number(amount) / 10 ** decimalPlaces;

  // Create a formatter that will display with currency's default decimal places
  const displayFormatter = new Intl.NumberFormat(data.settings.locale, {
    style: 'currency',
    currency: data.settings.currency,
  });

  // Format with appropriate decimal places
  return displayFormatter.format(amount);
};

const formatPercentage = (data, amount) => new Intl.NumberFormat(data.settings.locale, {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(amount / 100);

/**
 * Reset the discount validation state
 * @param data
 */
const resetFeedback = (data) => {
  data.elements.$feedbackContainer.addClass(classes.hidden);
  data.elements.$feedbackPending.addClass(classes.hidden);
  data.elements.$feedbackSuccessReduction.addClass(classes.hidden);
  data.elements.$feedbackSuccessSetPrice.addClass(classes.hidden);
  data.elements.$feedbackError.addClass(classes.hidden);
  data.elements.$element.preventDoubleSubmission('reEnable');
};

/**
 * Reset the personal information fields
 * @param data
 */
const resetPersonalInformation = (data) => {
  data.elements.$personalInformationContainer.addClass(classes.hidden);
  data.elements.$personalInformationFamilyNameContainer.addClass(classes.hidden);
  data.elements.$personalInformationGivenNameContainer.addClass(classes.hidden);
  data.elements.$personalInformationDateOfBirthContainer.addClass(classes.hidden);

  data.elements.$personalInformationFamilyName.empty();
  data.elements.$personalInformationGivenName.empty();
  data.elements.$personalInformationDateOfBirth.empty();
};

/**
 * Get the selected discount information
 * @param data
 * @returns {object}
 */
const getSelectedDiscountInformation = (data) => {
  if (!data.elements.$select.val()) {
    return null;
  }

  const $selectedDiscount = data.elements.$select.find(':selected');

  return {
    label: $selectedDiscount.text().trim(),
    type: $selectedDiscount.attr('data-type'),
    amount: Number($selectedDiscount.attr('data-amount')),
    validation: $selectedDiscount.attr('data-validation') === 'true',
    validationFields: $selectedDiscount.attr('data-validation-fields')?.split(',') ?? [],
    identifiers: $selectedDiscount.attr('data-identifiers')?.split(',') ?? [],
  };
};

/**
 * Toggle the identifier field
 * @param data
 */
const toggleIdentifier = (data) => {
  const selectedDiscount = getSelectedDiscountInformation(data);
  const isVisible = selectedDiscount?.validationFields.includes(validationFields.IDENTIFIER) ?? false;

  data.elements.$identifierContainer.toggleClass(classes.hidden, !isVisible);
  data.elements.$identifier.prop('disabled', !isVisible);
  data.elements.$submit.toggleClass(classes.hidden, !isVisible);
  data.elements.$element.preventDoubleSubmission('reEnable');
};

/**
 * Toggle the personal information fields
 * @param data
 */
const togglePersonalInformation = (data) => {
  const selectedDiscount = getSelectedDiscountInformation(data);

  if (!selectedDiscount?.validation) {
    return;
  }

  if (!selectedDiscount.validationFields.includes(validationFields.FAMILY_NAME)
    && !selectedDiscount.validationFields.includes(validationFields.GIVEN_NAME)
    && !selectedDiscount.validationFields.includes(validationFields.DATE_OF_BIRTH)) {
    return;
  }

  data.elements.$personalInformationContainer.removeClass(classes.hidden);

  if (selectedDiscount.validationFields.includes(validationFields.FAMILY_NAME)) {
    data.elements.$personalInformationFamilyNameContainer.removeClass(classes.hidden);
  }

  if (selectedDiscount.validationFields.includes(validationFields.GIVEN_NAME)) {
    data.elements.$personalInformationGivenNameContainer.removeClass(classes.hidden);
  }

  if (selectedDiscount.validationFields.includes(validationFields.DATE_OF_BIRTH)) {
    data.elements.$personalInformationDateOfBirthContainer.removeClass(classes.hidden);
  }
};

/**
 * Reset the plugin
 * @param data
 */
const reset = (data) => {
  resetFeedback(data);
  resetPersonalInformation(data);
};

/**
 * Get the family name
 * @param data
 * @returns {string}
 */
const getFamilyName = (data) => {
  if (data.state.shouldUsePersonalInformationCallbacks) {
    return data.settings.callbacks.getFamilyName();
  }

  return data.settings.registrantPersonalInformation.person.familyName;
};

/**
 * Get the given name
 * @param data
 * @returns {string}
 */
const getGivenName = (data) => {
  if (data.state.shouldUsePersonalInformationCallbacks) {
    return data.settings.callbacks.getGivenName();
  }

  return data.settings.registrantPersonalInformation.person.givenName;
};

/**
 * Get the date of birth
 * @param data
 * @returns {string}
 */
const getDateOfBirth = (data) => {
  if (data.state.shouldUsePersonalInformationCallbacks) {
    return data.settings.callbacks.getDateOfBirth();
  }

  return data.settings.registrantPersonalInformation.person.dateOfBirth;
};

/**
 * Check if the discount can be validated
 * @param data
 * @returns {boolean}
 */
const canValidate = (data) => {
  const selectedDiscount = getSelectedDiscountInformation(data);

  if (!selectedDiscount?.validation) {
    return false;
  }

  if (selectedDiscount.validationFields.includes(validationFields.IDENTIFIER)
    && !data.elements.$identifier.val()) {
    return false;
  }

  if (selectedDiscount.validationFields.includes(validationFields.FAMILY_NAME)
    && !getFamilyName(data)) {
    return false;
  }

  if (selectedDiscount.validationFields.includes(validationFields.GIVEN_NAME)
    && !getGivenName(data)) {
    return false;
  }

  if (selectedDiscount.validationFields.includes(validationFields.DATE_OF_BIRTH)
    && !getDateOfBirth(data)) {
    return false;
  }

  return true;
};

/**
 * Check if the discount can be automatically validated
 * @param data
 * @returns {boolean}
 */
const canAutomaticallyValidate = (data) => {
  if (!canValidate(data)) {
    resetFeedback(data);
    return false;
  }

  const selectedDiscount = getSelectedDiscountInformation(data);

  if (selectedDiscount?.validationFields.includes(validationFields.IDENTIFIER)) {
    return false;
  }

  return true;
};

/**
 * Get the fields
 * @param data
 * @returns {array}
 */
const getFields = (data) => {
  const selectedDiscount = getSelectedDiscountInformation(data);

  if (!selectedDiscount) {
    return [];
  }

  const fields = [];

  if (selectedDiscount.validationFields.includes(validationFields.FAMILY_NAME)) {
    fields.push({
      field: 'family_name',
      value: getFamilyName(data),
    });
  }

  if (selectedDiscount.validationFields.includes(validationFields.GIVEN_NAME)) {
    fields.push({
      field: 'given_name',
      value: getGivenName(data),
    });
  }

  if (selectedDiscount.validationFields.includes(validationFields.DATE_OF_BIRTH)) {
    fields.push({
      field: 'dob',
      value: getDateOfBirth(data),
    });
  }

  if (selectedDiscount.validationFields.includes(validationFields.IDENTIFIER)) {
    fields.push({
      field: 'identifier',
      value: data.elements.$identifier.val(),
    });
  }

  return fields;
};

/**
 * Set the reduction success feedback
 * @param data
 * @param formattedAmount
 */
const setReductionSuccessFeedback = (data, formattedAmount) => {
  const errorText = data.elements.$feedbackSuccessReductionTextOriginal
    .text()
    .replace('%amount%', formattedAmount);
  const $newFeedbackSuccessReductionText = data.elements.$feedbackSuccessReductionTextOriginal
    .clone()
    .text(errorText)
    .append(data.elements.$feedbackSuccessReductionTextPencilEditIcon);

  data.elements.$feedbackContainer.removeClass(classes.hidden);
  data.elements.$feedbackSuccessReduction.removeClass(classes.hidden);
  data.elements.$feedbackSuccessReductionText.replaceWith($newFeedbackSuccessReductionText);

  data.elements.$feedbackSuccessReductionText = $newFeedbackSuccessReductionText;
};

/**
 * Set the set price success feedback
 * @param data
 * @param formattedAmount
 */
const setSetPriceSuccessFeedback = (data, formattedAmount) => {
  const errorText = data.elements.$feedbackSuccessSetPriceTextOriginal
    .text()
    .replace('%amount%', formattedAmount);
  const $newFeedbackSuccessSetPriceText = data.elements.$feedbackSuccessSetPriceTextOriginal
    .clone()
    .text(errorText)
    .append(data.elements.$feedbackSuccessSetPriceTextPencilEditIcon);

  data.elements.$feedbackContainer.removeClass(classes.hidden);
  data.elements.$feedbackSuccessSetPrice.removeClass(classes.hidden);
  data.elements.$feedbackSuccessSetPriceText.replaceWith($newFeedbackSuccessSetPriceText);

  data.elements.$feedbackSuccessSetPriceText = $newFeedbackSuccessSetPriceText;
};

/**
 * Set the success state
 * @param data
 */
const setSuccessState = (data) => {
  const selectedDiscount = getSelectedDiscountInformation(data);

  resetFeedback(data);

  if (!selectedDiscount) {
    return;
  }

  switch (selectedDiscount.type) {
    case discountTypes.PERCENTAGE:
      setReductionSuccessFeedback(data, formatPercentage(data, selectedDiscount.amount));
      break;
    case discountTypes.FIXED:
      setReductionSuccessFeedback(data, formatCurrency(data, selectedDiscount.amount));
      break;
    case discountTypes.SET_PRICE:
      setSetPriceSuccessFeedback(data, formatCurrency(data, selectedDiscount.amount));
      break;
    default:
      // do nothing
  }
};

/**
 * Validate discount code
 * @param data
 */
const validate = debounce((data) => {
  resetFeedback(data);

  if (!canValidate(data)) {
    return;
  }

  const selectedDiscount = getSelectedDiscountInformation(data);

  data.elements.$feedbackContainer.removeClass(classes.hidden);
  data.elements.$feedbackPending.removeClass(classes.hidden);

  data.requests.validate = $.ajax({
    url: data.settings.routes.validation,
    type: 'POST',
    data: {
      validator_type: 'validation-list',
      identifiers: selectedDiscount.identifiers,
      fields: getFields(data),
    },
  })
    .always(() => {
      data.requests.validate = null;
      data.elements.$feedbackPending.addClass(classes.hidden);
    })
    .done((response) => {
      if (!response.valid) {
        data.elements.$feedbackError.removeClass(classes.hidden);
        return;
      }

      setSuccessState(data);
    })
    .fail(() => {
      data.elements.$snackBar.snackBar('add', {
        type: 'danger',
        content: data.settings.i18n.genericError,
      });
    });
}, 500);

/**
 * Update the discount validation
 * @param data
 */
const updatePersonalInformationValues = (data) => {
  const selectedDiscount = getSelectedDiscountInformation(data);

  if (!selectedDiscount?.validation) {
    return;
  }

  if (selectedDiscount.validationFields.includes(validationFields.FAMILY_NAME)) {
    const familyName = getFamilyName(data);

    if (familyName) {
      data.elements.$personalInformationFamilyName
        .removeClass(classes.incomplete)
        .text(familyName);
    } else {
      data.elements.$personalInformationFamilyName
        .addClass(classes.incomplete)
        .text(data.settings.i18n.incomplete);
    }
  }

  if (selectedDiscount.validationFields.includes(validationFields.GIVEN_NAME)) {
    const givenName = getGivenName(data);

    if (givenName) {
      data.elements.$personalInformationGivenName
        .removeClass(classes.incomplete)
        .text(givenName);
    } else {
      data.elements.$personalInformationGivenName
        .addClass(classes.incomplete)
        .text(data.settings.i18n.incomplete);
    }
  }

  if (selectedDiscount.validationFields.includes(validationFields.DATE_OF_BIRTH)) {
    const dateOfBirth = getDateOfBirth(data);

    if (dateOfBirth) {
      data.elements.$personalInformationDateOfBirth
        .removeClass(classes.incomplete)
        .text(dateOfBirth);
    } else {
      data.elements.$personalInformationDateOfBirth
        .addClass(classes.incomplete)
        .text(data.settings.i18n.incomplete);
    }
  }

  if (canAutomaticallyValidate(data)) {
    validate(data);
  }
};

/**
 * Select the discount
 * @param data
 */
const selectDiscount = (data) => {
  const selectedDiscount = getSelectedDiscountInformation(data);

  resetFeedback(data);
  resetPersonalInformation(data);
  toggleIdentifier(data);
  togglePersonalInformation(data);

  if (!selectedDiscount) {
    return;
  }

  if (selectedDiscount.validation) {
    updatePersonalInformationValues(data);
  } else {
    setSuccessState(data);
  }
};

/**
 * Serialize the discount validation data
 * @param data
 * @returns {string}
 */
const serialize = (data) => {
  const formData = data.elements.$element.serializeArray();
  const selectedDiscount = getSelectedDiscountInformation(data);

  if (selectedDiscount?.validation) {
    const fields = getFields(data);

    formData.push({ name: `${formName}[validator_type]`, value: 'validation-list' });

    fields.forEach((field, index) => {
      formData.push({ name: `${formName}[fields][${index}][field]`, value: field.field });
      formData.push({ name: `${formName}[fields][${index}][value]`, value: field.value });
    });

    selectedDiscount.identifiers.forEach((identifier, index) => {
      formData.push({ name: `${formName}[identifiers][${index}]`, value: identifier });
    });
  }

  return $.param(formData);
};

/**
 * Cancel any active requests being made
 * @param data
 */
const cancelActiveRequests = (data) => {
  if (data.requests.validate) {
    data.requests.validate.abort();
  }
};

/**
 * Clear the plugin's contents
 */
const clear = (data) => {
  cancelActiveRequests(data);
  data.elements.$element.empty();
};

/**
 * Setup the plugin
 * @param data
 * @param html
 */
const setup = (data, html) => {
  data.elements.$element.html(html);

  $.extend(data.elements, getElements(data));

  reset(data);

  data.elements.$select.on('change', () => selectDiscount(data));
  data.elements.$element
    .preventDoubleSubmission()
    .on('submit', (event) => {
      if (!data.elements.$element[0].checkValidity()) {
        return;
      }

      event.preventDefault();
      validate(data);
    });

  data.state.setup = true;

  triggerHook(data, 'setup');
};

const methods = {
  toggleVisibility,
  setup,
  initializeContent: setup,
  shouldUsePersonalInformationCallbacks: (data) => {
    data.state.shouldUsePersonalInformationCallbacks = true;
  },
  update: (data) => {
    if (!data.state.setup) {
      return;
    }

    updatePersonalInformationValues(data);
  },
  clear,
  cancelActiveRequests,
};

const getMethods = {
  serialize: (data) => {
    if (!data.state.setup) {
      return '';
    }

    return serialize(data);
  },
};

/**
 * Call one of our custom plugin methods on each given jQuery object
 * @param $element
 * @param method
 * @param methodParameters
 * @returns {jQuery}
 */
const callPluginMethod = ($element, method, ...methodParameters) => {
  const data = $element.data(pluginName);

  if (data) {
    methods[method](data, ...methodParameters);
  }

  return $element;
};

/**
 * Fire up
 * @param $element
 * @param options
 */
const initialize = ($element, options) => {
  if ($element.data(pluginName)) {
    return;
  }

  const settings = $.extend(true, {}, defaults, options);
  const elements = {
    $element,
  };
  const requests = {
    validate: null,
  };
  const data = {
    settings,
    elements,
    requests,
    state: {
      setup: false,
      shouldUsePersonalInformationCallbacks: false,
    },
  };

  data.elements.$element.data(pluginName, data);
  triggerHook(data, 'initialized');
};

/**
 * Plugin
 * @param methodOrOptions
 * @param methodParameters
 * @returns {jQuery}
 */
const plugin = function plugin(methodOrOptions, ...methodParameters) {
  if (getMethods[methodOrOptions]) {
    const $element = $(this);
    const data = $element.data(pluginName);

    if (!data) {
      return false;
    }

    return getMethods[methodOrOptions](data, ...methodParameters);
  }

  if (methods[methodOrOptions]) {
    return this
      .each((index, element) => callPluginMethod($(element), methodOrOptions, ...methodParameters));
  }

  if (typeof methodOrOptions === 'object' || !methodOrOptions) {
    return this.each((index, element) => initialize($(element), methodOrOptions));
  }

  return $.error(`Method ${methodOrOptions} does not exist in ${pluginName}`);
};

plugin.methods = methods;
plugin.getMethods = getMethods;

$(() => {
  if (pluginName in $.fn) {
    return;
  }

  $.extend($.fn, {
    [pluginName]: plugin,
  });
});
